function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { getSite } from '@edreamsodigeo/frontend-app-config';
import { loadIntlPolyfill } from '../polyfills/Intl';
import { createAppProviderProps } from './AppProvider.props';
import { AppProvider } from './AppProvider';
import page from '../boundaries/page/oneFrontActions';
const parseSitesConfig = sitesConfig => {
  const {
    locale
  } = sitesConfig;
  const importLocale = () => {
    return import(/* webpackChunkName: "[request]" */
    `date-fns/locale/${locale.dates || locale.i18n}/index.js`);
  };
  return importLocale().then(dateFnsLocale => ({
    ...sitesConfig,
    locale: {
      ...sitesConfig.locale,
      dates: dateFnsLocale.default
    }
  })).catch(() => {
    // retry importing same locale
    return importLocale().then(dateFnsLocale => ({
      ...sitesConfig,
      locale: {
        ...sitesConfig.locale,
        dates: dateFnsLocale.default
      }
    })).catch(error => {
      throw error;
    });
  });
};
const buildAppProviderProps = ({
  locale,
  brandCode,
  websiteCode
}, appProviderProps) => ({
  ...appProviderProps,
  brand: brandCode,
  dateLocale: locale.dates,
  currencyLocale: locale.currency,
  currencyFormat: locale.currencyFormat,
  site: websiteCode,
  locale: locale.i18n
});
const render = (App, appProviderProps, appProps) => {
  const rootEl = document.getElementById('frontech_app_container');
  if (rootEl) {
    ReactDOM.render(/*#__PURE__*/React.createElement(App, {
      appProviderProps: appProviderProps,
      appProps: appProps
    }), rootEl);
  }
};
export const createShell = (App, {
  options,
  createIntegration = () => ({}),
  createExports = () => ({}),
  createAppProps = () => ({})
}) => {
  const buildExports = (integration, appProviderProps) => {
    const {
      baseProps
    } = appProviderProps;
    const {
      errorHandler
    } = baseProps;
    return {
      ...createExports(integration),
      // TODO: remove when totally deprecated legacy error handling management
      error: {
        restore: errorHandler.restore
      },
      // Export page for all
      page
    };
  };
  const dispatchError = error => {
    if (window?.Odigeo?.trigger) {
      window.Odigeo.trigger('frontend-shell:javascript-error', error);
    }
  };
  let shellPromise = null;
  let cachedShellOptions = null;
  const ShellApp = ({
    appProviderProps,
    appProps
  }) => /*#__PURE__*/React.createElement(AppProvider, _extends({}, appProviderProps, {
    disableTracking: options.disableTracking
  }), /*#__PURE__*/React.createElement(App, appProps));
  ShellApp.propTypes = {
    appProviderProps: PropTypes.object,
    appProps: PropTypes.object
  };
  const loadShell = async ({
    siteConfig,
    appProviderProps,
    integration,
    exports
  }) => {
    const appProps = createAppProps(integration);
    return Promise.all([loadIntlPolyfill(siteConfig.locale.currency.locale, window.Odigeo.SessionScope.logger), parseSitesConfig(siteConfig)]).then(([, siteConfig]) => render(ShellApp, buildAppProviderProps(siteConfig, appProviderProps), appProps)).then(() => exports);
  };
  const getSiteConfig = () => {
    const SessionScope = window.Odigeo.SessionScope;
    const brand = SessionScope.brandNewNomenclature;
    const localeArr = SessionScope.locale.split('_');
    const locale = localeArr[0] + (localeArr.length > 1 ? '-' + localeArr[1] : '');
    return getSite(brand, locale);
  };
  const createShellOptions = async shellOptions => {
    const siteConfig = getSiteConfig();
    const appProviderProps = await createAppProviderProps(siteConfig, shellOptions);
    const integration = createIntegration({
      storageClient: appProviderProps.baseProps?.storageClient
    });
    const exports = buildExports(integration, appProviderProps);
    return {
      siteConfig,
      integration,
      appProviderProps,
      exports
    };
  };
  return {
    async execute(action) {
      const shellOptions = cachedShellOptions || (await createShellOptions(options));
      if (!window.Odigeo.AB.isFrontendShellRolloutActive) {
        return Promise.resolve(shellOptions.exports);
      }
      shellPromise = shellPromise || loadShell(shellOptions);
      return await shellPromise.then(action).catch(dispatchError);
    }
  };
};